import { PaymentItem } from './payment-providers.types';
import { PaymentMethod } from './payment-request.interface';

/**
 * PayMe Seller Incorporation Types (Israel)
 * Reference: https://docs.payme.io/docs/guides/branches/main/s7t2zmnz2642i-seller-incorporation-type-il
 */
export enum PaymeSellerIncorporationType {
  /** Sole Proprietorship / עוסק מורשה */
  SOLE_PROPRIETORSHIP = 2,

  /** Incorporated Company / חברה בע"מ */
  INCORPORATED_COMPANY = 3,

  /** Exempt Company / חברה פטורה */
  EXEMPT_COMPANY = 5,
}

/**
 * Additional seller details (for companies with multiple individuals)
 */
export interface IPaymeSellerAdditionalDetails {
  first_name?: string;
  last_name?: string;
  social_id?: string;
  type?: number[];
  email?: string;
}

/**
 * DTO for creating a PayMe seller account
 * Reference: https://docs.payme.io/docs/payme-public/ZG9jOjI1MzUxNzgy-create-seller
 */
export interface CreatePaymeSellerDto {
  // ============ Personal Information ============
  seller_first_name: string;
  seller_last_name: string;
  // seller_social_id must be 9-10 digits
  seller_social_id: string;
  // seller_birthdate must be in format dd/mm/yyyy
  seller_birthdate: string;
  // seller_social_id_issued must be in format dd/mm/yyyy
  seller_social_id_issued: string;
  // 0 = Male, 1 = Female
  seller_gender: 0 | 1;
  seller_email: string;
  // seller_phone must be in format +9720XXXXXXXXX
  seller_phone: string;

  // ============ Bank Information ============
  seller_bank_code: number | undefined;
  seller_bank_branch: number | undefined;
  seller_bank_account_number: string | undefined;

  // ============ Business Information ============
  seller_description: string;
  seller_site_url: string;
  seller_person_business_type: number; // MCC code
  seller_inc: PaymeSellerIncorporationType;
  seller_inc_code?: string; // Required when seller_inc is not 1
  // 1 = Card not present (online), 2 = Card present
  seller_retail_type?: number;
  seller_merchant_name: string;
  seller_merchant_name_en?: string;

  // ============ Address Information ============
  seller_address_city: string;
  seller_address_street: string;
  seller_address_street_number: string;
  // seller_address_country must be ISO 3166 country code (e.g., IL)
  seller_address_country: string;

  // ============ Optional Fields ============
  market_fee?: number;
  language?: string; // 'he' or 'en'
  seller_plan?: string;
  // seller_additional_details?: PaymeSellerAdditionalDetails;
  seller_dba?: string; // Legal name in Hebrew
  seller_dba_en?: string; // Legal name in English
}

/**
 * Response from PayMe create seller API
 */
export interface CreatePaymeSellerResponse {
  status_code: number; // 0 = success, 1 = error
  seller_payme_id?: string;
  seller_payme_secret?: string;
  seller_public_key?: {
    uuid: string;
    description: string;
    is_active: boolean;
  };
  seller_id?: string;
  seller_dashboard_signup_link?: string;
  status_error_code?: string;
  status_error_details?: string;
}

/**
 * Response from PayMe Retrieve Seller's Additional Services Data
 */
export interface GetVASResponse {
  status_code: number; // 0 = success, 1 = error
  seller_payme_id?: string;
  items: Array<{
    vas_description: string;
    vas_type: string;
    vas_api_key: string;
    vas_is_active: true;
  }>;
}

export interface IPaymeSessionResponse {
  sellerKey: string;
  testMode: boolean;
  paymentRequestUuid: string;
  saleData: IPaymeSaleData;
  installments?: number;
  // 'before-appointment': card saved, backend auto-charges at scheduledChargeDate.
  // 'save-card': card saved, the business charges manually (no auto-charge).
  chargeMode?: 'immediate' | 'before-appointment' | 'save-card';
  scheduledChargeDate?: string;
  cancellationPolicyText?: string;
  /** What is being paid for — lets the form word the save-card step for a product order */
  purpose?: 'booking' | 'product-order';
}

export interface IPaymeSaleData {
  payerFirstName: string;
  payerLastName: string;
  payerEmail?: string;
  payerPhone: string;
  total: {
    label: string;
    amount: {
      currency: string;
      value: string;
    };
  };
  items: PaymentItem[];
  paymentMethods?: Pick<Record<PaymePaymentMethods, boolean>, PaymePaymentMethods.Bit>;
}

export interface IPaymeGenerateSaleRequest {
  paymentRequestUuid: string;
  paymentMethod: PaymePaymentMethods;
  email?: string;
  token?: string;
  installments?: number;
}

export interface IPaymeSaveCardRequest {
  paymentRequestUuid: string;
  // Permanent JSAPI token (created with tokenIsPermanent: true)
  token: string;
  email?: string;
  // Customer accepted the cancellation policy / delayed charge terms
  consent: boolean;
}

export interface IPaymeSaveCardResponse {
  success: boolean;
  // Present in before-appointment mode only
  scheduledChargeDate?: string;
  error?: string;
}

export enum PaymePaymentMethods {
  CreditCard = 'credit-card',
  BankTransfer = 'bank-transfer',
  Cash = 'cash',
  Bit = 'bit',
  /** A physical PayMe card terminal (card-present) */
  Pos = 'pos',
  // ApplePay = 'apple-pay',
  // GooglePay = 'google-pay'
}

export interface IPaymeGenerateSaleArgs {
  // MPL. Your private key in PayMe system.
  seller_payme_id: string;
  /** Sale final price.
   For example, if the price is 50.75 (max 2 decimal points) the value that needs to be sent is 5075.
   Note that the minimum value is 500. **/
  sale_price: number;
  currency: 'ILS';
  /** Short name and description of the product/service.
   * This text will be shown in the invoice as well, if the seller has enabled the invoices module in his account panel.
   * Limited to 500 characters **/
  product_name: string;
  //Merchant's unique sale ID for correlation with us
  transaction_id?: string;
  // Callback response to your page regarding call to our API. Default value is taken from the Merchant's settings.
  // Note that you may not send a "localhost" URL as value
  sale_callback_url?: string;
  /**
   * Amount of installments for the sale. We allow you to manually set the number of installments or to specify a top limit of installments for the buyer to choose from while paying.
   * Setting a fixed number of installments is simply entering a number between 1 and 12.
   * The sale will be initiated with this number of installments and the buyer will not be able to change it.
   * To set a range for the buyer to choose from, enter the following:
   * 103 - allow up to 3 installments
   * 106 - allow up to 6 installments
   * 109 - allow up to 9 installments
   * 112 - allow up to 12 installments
   *
   * Example:
   * 1 **/
  installments?: number;
  // Flag to send email and/or SMS notifications
  sale_send_notification?: boolean;
  // In case sale send notification is true provide the address to send email notification
  sale_email?: string;
  // In case sale send notification is true, provide the phone number to send SMS notification, if the seller has enabled the SMS module in his account panel
  sale_mobile?: string;
  // The name that will be displayed when sending a notification
  sale_name?: string;
  sale_payment_method: PaymePaymentMethods;
  items?: Array<{
    // Name of items Maximum length: 26 characters
    name: string;
    // Number of items Maximum length: 12 digits
    quantity: number;
    // Price of single unit in pennies Maximum length: 12 digits
    unit_price: number;
    // Total Price After Discount Maximum length: 12 digits
    total: number;
    // In negative pennies amount Maximum length: 12 digits
    discount_total?: number;
    // Maximum length: 26 characters
    description?: string;
  }>;
  buyer_name?: string;
  buyer_email?: string;
  buyer_phone?: string;
  // For JSApi token
  buyer_key?: string;
}

export type IPaymeGenerateSaleResponse = {
  success: true;
  transactionId: string;
  price: number;
  paymentMethod: PaymentMethod;
  date: Date | string;
  saleUrl?: string;
  invoiceUrl?: string;

} | {
  success: false;
  error?: string;
}

export interface IPaymePosTransaction {
  seller_payme_id: string;
  product_name: string;
  // In Agorot
  sale_price: number;
  currency: 'ILS';
  sale_type: 'sale';
  payment: {
    method: PaymentMethod.Cash | PaymentMethod.BankTransfer;
    auth_number?: string;
    // ISO Format "2025-12-04"
    release_date?: string;
  },
  items: IPaymeGenerateSaleArgs['items'],
  customer: {
    name: string;
    email: string;
    // +972...
    phone: string;
  },
  language: 'he';
  // Flag to send email and/or SMS notifications
  sale_send_notification?: boolean;
  // In case sale send notification is true provide the address to send email notification
  sale_email?: string;
  // In case sale send notification is true, provide the phone number to send SMS notification, if the seller has enabled the SMS module in his account panel
  sale_mobile?: string;
  // The name that will be displayed when sending a notification
  sale_name?: string;
  buyer_name?: string;
  buyer_email?: string;
  buyer_phone?: string;
}

export interface IPaymeCallbackResponse {
  payme_status: 'success' | 'error';
  payme_sale_id: string;
  sale_created: string;
  payme_sale_status: 'completed' | 'failed' | string;
  sale_status: 'completed' | 'failed' | string;
  /** e.g. 'sale-complete' / 'sale-failure' */
  notify_type?: string;
  status_error_code?: string;
  status_error_details?: string;
  buyer_card_mask: string;
  installments: string;
  transaction_first_payment: string;
  transaction_periodical_payment: string;
  sale_invoice_url: string;
  price: number;
  sale_payment_method: string;
  /** Our id sent with the sale — the payment request's _id */
  transaction_id?: string;
}
